'use client';

import { useState, useEffect } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { SyncIcon } from '@/shared/ui/display/EmojiIcon';

const iconButtonSx = {
  color: 'text.secondary',
  '&:hover': {
    backgroundColor: 'action.hover',
    color: 'text.primary',
  },
} as const;

/**
 * RFQ_SYNC_BASE_URL + RFQ_SYNC_API_KEY 가 서버에 설정된 경우에만 표시.
 * 검색바 아이콘 → 모달에서 미소 주문 ID 입력 후 동기화(BFF).
 */
export function OrderSyncIconButton() {
  const [enabled, setEnabled] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/v1/admin/order-sync/config');
        if (!res.ok) return;
        const data = (await res.json()) as { enabled?: boolean };
        if (!cancelled && data.enabled) setEnabled(true);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCloseModal = () => {
    if (submitting) return;
    setOpen(false);
  };

  const handleSync = async () => {
    const trimmed = orderId.trim();
    if (!trimmed) {
      setSnackbar({ message: '미소 주문 ID를 입력하세요.', severity: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/admin/order-sync/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: trimmed }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setSnackbar({
          message: data.error ?? '동기화 요청에 실패했습니다.',
          severity: 'error',
        });
      } else {
        setSnackbar({ message: '동기화를 요청했습니다.', severity: 'success' });
        setOrderId('');
        setOpen(false);
      }
    } catch {
      setSnackbar({ message: '네트워크 오류가 발생했습니다.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (configLoading || !enabled) return null;

  return (
    <>
      <Tooltip title="주문 동기화 (RFQ)">
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          sx={iconButtonSx}
          aria-label="주문 동기화"
        >
          <SyncIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="xs">
        <DialogTitle>주문 동기화</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="미소 주문 ID"
            placeholder="예: 1871830"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            fullWidth
            variant="outlined"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSync();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} disabled={submitting}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSync()}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          >
            동기화
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar != null}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
